import { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { Eye, Pause, Play, Trash2 } from "lucide-react";

import { ApiError } from "@/api/client";
import { useDeleteOrganization, useImpersonate, useOrganizations, useSuspend, useUnsuspend } from "@/api/hooks";
import { AppShell } from "@/components/app-shell";
import { DeleteOrganizationDialog } from "@/components/delete-organization-dialog";
import { HealthPill, StatusPill } from "./organizations";

/**
 * ONE TENANT, AND THE FOUR THINGS STAFF CAN DO TO IT.
 *
 * Suspend (reversible), unsuspend, impersonate (read-only), delete (irreversible, and only
 * once suspended). Each one demands a reason, because each one lands in the audit log next
 * to the name of the person who ordered it.
 */
export function OrganizationDetailPage() {
  const { orgId } = useParams({ from: "/organizations/$orgId" });
  const navigate = useNavigate();

  // The list is the read model; the console is small and the list is cheap. A dedicated
  // "get one organization" endpoint is not in the contract, and the contract is the contract.
  const { data, isPending } = useOrganizations("", "");
  const org = data?.data.find((o) => o.id === orgId);

  const suspend = useSuspend();
  const unsuspend = useUnsuspend();
  const remove = useDeleteOrganization();
  const impersonate = useImpersonate();

  const [reason, setReason] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (isPending) {
    return (
      <AppShell>
        <p className="text-[var(--color-muted)]">Loading…</p>
      </AppShell>
    );
  }

  if (!org) {
    return (
      <AppShell>
        <p className="panel p-6 text-[var(--color-muted)]">
          No such organization. It may have been deleted — check the audit log.
        </p>
      </AppShell>
    );
  }

  const reasonGiven = reason.trim().length >= 8;
  const busy = suspend.isPending || unsuspend.isPending || impersonate.isPending;
  const actionError = [suspend.error, unsuspend.error, impersonate.error].find(Boolean);

  return (
    <AppShell>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{org.name}</h1>
          <p className="tabular mt-1 text-sm text-[var(--color-faint)]">{org.slug}</p>
          <div className="mt-3 flex items-center gap-3">
            <StatusPill status={org.status} />
            <HealthPill health={org.health} />
            <span className="text-sm capitalize text-[var(--color-muted)]">{org.plan} plan</span>
          </div>
        </div>

        <dl className="tabular grid grid-cols-3 gap-x-8 gap-y-2 text-sm">
          <Stat label="Members" value={org.usage.members} />
          <Stat label="Sites" value={org.usage.sites} />
          <Stat label="Tanks" value={org.usage.tanks} />
          <Stat label="Generators" value={org.usage.generators} />
          <Stat label="API keys" value={org.usage.api_keys ?? 0} />
          <Stat label="Refills 30d" value={org.usage.refills_last_30d} />
        </dl>
      </div>

      {org.status === "suspended" && org.suspension_reason && (
        <p className="panel mt-6 border-[var(--color-warn)] bg-[var(--color-warn-wash)] p-4 text-sm">
          <strong className="font-semibold">Suspended.</strong> {org.suspension_reason}
          {org.suspended_at && (
            <span className="tabular ml-2 text-[var(--color-muted)]">
              ({new Date(org.suspended_at).toLocaleString()})
            </span>
          )}
        </p>
      )}

      {/* THE REASON IS THE FIRST FIELD ON THE PAGE, not a modal afterthought. Every button
          below is inert until it is filled: you say why BEFORE you decide what. */}
      <section className="panel mt-6 p-6">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--color-faint)]">
          Staff actions
        </h2>

        <label className="mt-4 block text-sm">
          <span className="text-[var(--color-muted)]">
            Reason — written to the append-only audit log, with your name, permanently
          </span>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Non-payment after 60 days, TKT-4402"
            aria-label="Reason"
            className="mt-1 w-full rounded border border-[var(--color-rule-strong)] bg-[var(--color-ink)] px-3 py-2 text-[var(--color-text)]"
          />
        </label>

        <div className="mt-4 flex flex-wrap gap-3">
          {org.status === "active" ? (
            <button
              type="button"
              disabled={!reasonGiven || busy}
              onClick={() => suspend.mutate({ orgId: org.id, reason: reason.trim() })}
              className="flex items-center gap-2 rounded border border-[var(--color-warn)] px-4 py-2 text-sm font-medium text-[var(--color-warn)] hover:bg-[var(--color-warn-wash)] disabled:opacity-40"
            >
              <Pause aria-hidden className="size-4" />
              Suspend
            </button>
          ) : (
            <button
              type="button"
              disabled={!reasonGiven || busy}
              onClick={() => unsuspend.mutate({ orgId: org.id, reason: reason.trim() })}
              className="flex items-center gap-2 rounded border border-[var(--color-wet)] px-4 py-2 text-sm font-medium text-[var(--color-wet)] hover:bg-[var(--color-wet-wash)] disabled:opacity-40"
            >
              <Play aria-hidden className="size-4" />
              Lift suspension
            </button>
          )}

          <button
            type="button"
            disabled={!reasonGiven || busy}
            onClick={async () => {
              await impersonate.mutateAsync({ orgId: org.id, reason: reason.trim() });
              // The banner reads the session out of storage on mount, so a navigation is
              // what makes it appear. It cannot be dismissed once it does.
              await navigate({ to: "/organizations/$orgId", params: { orgId: org.id } });
              window.location.reload();
            }}
            className="flex items-center gap-2 rounded border border-[var(--color-rule-strong)] px-4 py-2 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-panel-high)] disabled:opacity-40"
          >
            <Eye aria-hidden className="size-4" />
            Impersonate (read-only)
          </button>

          <button
            type="button"
            disabled={busy}
            onClick={() => setDeleteOpen(true)}
            className="ml-auto flex items-center gap-2 rounded border border-[var(--color-dry)] px-4 py-2 text-sm font-medium text-[var(--color-dry)] hover:bg-[var(--color-dry-wash)]"
          >
            <Trash2 aria-hidden className="size-4" />
            Delete…
          </button>
        </div>

        {!reasonGiven && (
          <p className="mt-3 text-xs text-[var(--color-faint)]">
            Give a reason (8 characters or more) to enable these actions.
          </p>
        )}

        {actionError && (
          <p role="alert" className="mt-3 text-sm text-[var(--color-dry)]">
            {actionError instanceof ApiError ? actionError.message : "The action failed."}
          </p>
        )}
      </section>

      <DeleteOrganizationDialog
        org={org}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        pending={remove.isPending}
        error={remove.error instanceof ApiError ? remove.error.message : undefined}
        onConfirm={async (deleteReason) => {
          await remove.mutateAsync({ orgId: org.id, reason: deleteReason });
          setDeleteOpen(false);
          await navigate({ to: "/organizations" });
        }}
      />
    </AppShell>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-[var(--color-faint)]">{label}</dt>
      <dd className="text-lg">{value}</dd>
    </div>
  );
}
